import useCdcProps from '@/composables/useCdcProps'
import type { Product } from '@/types/products.interfaces'
import {
  ProductCompatibilityTag,
  PhpCompatibilityInfo,
} from '@/types/overrides.interfaces'

import {
  checkOverridesAvailability,
  parseShopOverrides,
  parsePsVersion,
  calculateProductCompatibilityTag,
  enhanceProductOverridesWithShopOverrides,
  getProductPhpCompatibilityInfo,
} from '@/utils/overrides'

import { normalizeVersion } from '@/utils/semver'

export enum SeverityLevel {
  LOW = 'low',
  HIGH = 'high',
}

/**
 * Composable to manage and check product overrides compatibility with the current shop's overrides configuration
 *
 * @returns Object containing override-related properties and methods
 */
export default function useOverrides() {
  const { xprops } = useCdcProps()

  // Extract shop environment information from CDC props
  // Current shop's PHP version
  const shopPhpVersion = normalizeVersion(xprops?.context?.php_version)
  // Current shop's PrestaShop version (parsed)
  // Context may differ between 'shop_version' and 'ps_version' depending on CDC version (marketplace vs mbo-installer)
  const shopPsVersion = parsePsVersion(xprops?.context?.shop_version || xprops?.context?.ps_version)

  // Whether the current shop cdc supports overrides functionality
  const overridesSupported = checkOverridesAvailability(xprops)

  // Parse shop overrides if supported, otherwise set to undefined
  // Shop overrides data organized by class, raw format, map and as list
  const {
    shopOverridesByClass,
    shopOverrides,
    shopOverridesList,
    shopOverridesMap,
  } = overridesSupported
    ? parseShopOverrides(xprops)
    : {
        shopOverridesByClass: {},
        shopOverrides: {},
        shopOverridesList: [],
        shopOverridesMap: new Map(),
      }

  /**
   * Determines the compatibility tag for a product
   * @param product - The product to check for compatibility
   * @returns the compatibility tag for the product, should be undefined if the tag cannot be determined
   */
  function getProductCompatibilityTag(
    product: Product,
  ): ProductCompatibilityTag | undefined {
    return calculateProductCompatibilityTag(
      product,
      shopOverridesMap,
      shopPsVersion,
      shopPhpVersion,
      overridesSupported,
    )
  }

  /**
   * Enhance product overrides with shop overrides
   * @param product - The product to enhance
   * @returns the product with enhanced overrides
   */
  function enhanceProductOverrides(product: Product): Product {
    // Early return if overrides are not supported or shop overrides are unavailable
    if (!overridesSupported || !shopOverrides) return product

    return enhanceProductOverridesWithShopOverrides(product, shopOverrides)
  }

  function productPhpCompatibility(product: Product): PhpCompatibilityInfo {
    return getProductPhpCompatibilityInfo(
      product,
      shopPsVersion,
      shopPhpVersion,
    )
  }

  function getProductIncompatibilitySeverity(product: Product): SeverityLevel {
    return product.compliance?.compatibilityTag ===
      ProductCompatibilityTag.TECHNICAL_SKILLS_REQUIRED
      ? SeverityLevel.HIGH
      : SeverityLevel.LOW
  }

  return {
    overridesSupported,
    shopOverridesByClass,
    shopOverrides,
    shopOverridesList,
    getProductCompatibilityTag,
    enhanceProductOverrides,
    getProductIncompatibilitySeverity,
    productPhpCompatibility,
  }
}
